import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  CalendarCheck, Activity, Sparkles, AlertTriangle, MessageCircle,
  ClipboardList, Smile, Frown, Meh, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import EntitlementPaywall from '../components/EntitlementPaywall';
import { EntitlementRequiredError } from '../lib/axios-interceptors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /patient/this-week — Family Care-Hub "What Changed This Week" digest.
 *
 * Blueprint Decision #35. Read-only single-screen pulse the family
 * decision-maker opens once a week. Aggregates check-ins, completed
 * visits, care-plan updates, care-circle activity, and red flags into
 * a single headline + 5 supporting sections.
 */
export default function FamilyDigest() {
  const { user, getAuthHeader } = useAuth();
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter 163 · If the backend returns 402 the axios interceptor throws
  // an EntitlementRequiredError which we catch and surface as a soft
  // paywall instead of rendering the raw JSON body as JSX (which
  // crashes React with "Objects are not valid as a React child").
  const [paywall, setPaywall] = useState(null);

  const patientId = user?.id;

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setPaywall(null);
    try {
      const { data } = await axios.get(`${API}/patient/${patientId}/digest/this-week`, getAuthHeader());
      setDigest(data);
    } catch (e) {
      if (e instanceof EntitlementRequiredError) {
        setPaywall({
          flag: e.entitlementRequired,
          message: e.userMessage,
        });
      } else {
        const raw = e?.response?.data?.detail;
        toast.error(typeof raw === 'string' ? raw : 'Could not load weekly digest');
      }
    } finally {
      setLoading(false);
    }
  }, [patientId, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const moodIcon = (mood) => {
    if (mood === 'good') return <Smile className="w-4 h-4 text-emerald-600" />;
    if (mood === 'concerning' || mood === 'rough') return <Frown className="w-4 h-4 text-rose-600" />;
    return <Meh className="w-4 h-4 text-slate-500" />;
  };

  return (
    <DashboardShell role="patient" data-testid="family-digest-page">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500">This week at a glance</div>
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">What Changed This Week</h1>
        <p className="text-sm text-slate-600 mt-1">
          A 7-day pulse for the family — visits completed, check-ins, plan changes, and any flags worth a conversation.
        </p>
      </header>

      {loading ? (
        <div data-testid="family-digest-loading" className="rounded-2xl bg-white p-8 border border-slate-100 text-slate-500">
          Loading your weekly digest…
        </div>
      ) : paywall ? (
        <EntitlementPaywall
          flag={paywall.flag}
          title="Your This-Week digest isn't turned on yet"
          message={paywall.message}
          primaryHref="/patient/dashboard"
          primaryLabel="Back to dashboard"
        />
      ) : !digest ? (
        <div className="rounded-2xl bg-white p-8 border border-slate-100">
          <p className="text-slate-500">Nothing to show yet.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* HEADLINE */}
          <section className="rounded-2xl bg-gradient-to-br from-cn-sage-900 to-slate-900 text-white p-6" data-testid="family-digest-headline">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cn-accent/80 mb-2">
              <Sparkles className="w-4 h-4" /> Headline
            </div>
            <p className="text-xl font-semibold leading-snug">{digest.headline}</p>
            <p className="text-[11px] text-slate-300 mt-3">
              {new Date(digest.window_start).toLocaleDateString()} → {new Date(digest.window_end).toLocaleDateString()}
            </p>
          </section>

          {/* RED FLAG (if present) */}
          {digest.red_flag && (
            <section className="rounded-2xl bg-amber-50 border border-amber-200 p-5" data-testid="family-digest-red-flag">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-amber-900">Readmission risk: {digest.red_flag.band}</div>
                  <p className="text-sm text-amber-800 mt-1">{digest.red_flag.advice}</p>
                </div>
              </div>
            </section>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* VISITS */}
            <section className="rounded-2xl bg-white border border-slate-100 p-5" data-testid="family-digest-visits">
              <div className="flex items-center gap-2 mb-3">
                <CalendarCheck className="w-4 h-4 text-cn-primary" />
                <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Visits</h2>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <KPI label="Completed" value={digest.visits.completed_count} testid="visits-completed" />
                <KPI label="Missed" value={digest.visits.missed_count} testid="visits-missed" />
                <KPI label="Upcoming" value={digest.visits.next_visit ? '1' : '0'} testid="visits-next" />
              </div>
              {digest.visits.completed.length > 0 && (
                <ul className="space-y-2 text-sm">
                  {digest.visits.completed.slice(0, 3).map((v) => (
                    <li key={v.id} className="flex items-center justify-between border-t border-slate-100 pt-2">
                      <span className="text-slate-700">{v.clinician_name} · {v.type}</span>
                      <span className="text-slate-500 text-xs">{new Date(v.start_time).toLocaleDateString()}</span>
                    </li>
                  ))}
                </ul>
              )}
              {digest.visits.next_visit && (
                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600">
                  <span className="font-semibold text-cypress-800">Next up:</span>{' '}
                  {digest.visits.next_visit.clinician_name} ·{' '}
                  {new Date(digest.visits.next_visit.start_time).toLocaleString()}
                </div>
              )}
            </section>

            {/* CHECK-INS */}
            <section className="rounded-2xl bg-white border border-slate-100 p-5" data-testid="family-digest-checkins">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4 text-cn-primary" />
                <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Check-ins</h2>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <KPI label="Submitted" value={digest.check_ins.submitted_count} testid="checkins-submitted" />
                <KPI label="Days streak" value={digest.check_ins.days_checked_in} testid="checkins-streak" />
                <KPI label="Concerns" value={digest.check_ins.mood_counts.concerning || digest.check_ins.mood_counts.rough || 0} testid="checkins-concerns" />
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                {Object.entries(digest.check_ins.mood_counts).map(([m, n]) => (
                  <span key={m} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                    {moodIcon(m)} {m}: {n}
                  </span>
                ))}
              </div>
              {digest.check_ins.most_recent_concerning && (
                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-amber-700">
                  Recent concern: "{(digest.check_ins.most_recent_concerning.notes || '').slice(0, 80) || 'noted'}"
                </div>
              )}
            </section>

            {/* CARE PLAN */}
            <section className="rounded-2xl bg-white border border-slate-100 p-5" data-testid="family-digest-care-plan">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardList className="w-4 h-4 text-cn-primary" />
                <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Care plan</h2>
              </div>
              {digest.care_plan.active_id ? (
                <>
                  <p className="text-sm text-slate-700">
                    Active plan: <span className="font-semibold">{digest.care_plan.active_diagnosis || '—'}</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    {digest.care_plan.new_this_week > 0
                      ? `${digest.care_plan.new_this_week} new plan(s) issued this week.`
                      : 'No new plan issued this week.'}
                  </p>
                  <a href="/patient/care-plan" className="inline-flex items-center gap-1 text-xs font-semibold text-cn-primary mt-3">
                    Open care plan <ChevronRight className="w-3 h-3" />
                  </a>
                </>
              ) : (
                <p className="text-sm text-slate-500">No active care plan yet. Open Care Plan to create one.</p>
              )}
            </section>

            {/* CARE CIRCLE */}
            <section className="rounded-2xl bg-white border border-slate-100 p-5" data-testid="family-digest-care-circle">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="w-4 h-4 text-cn-primary" />
                <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Care circle</h2>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <KPI label="Messages" value={digest.care_circle.messages_this_week} testid="circle-messages" />
                <KPI label="New members" value={digest.care_circle.new_members_this_week.length} testid="circle-new-members" />
              </div>
              {digest.care_circle.new_members_this_week.length > 0 && (
                <ul className="text-xs text-slate-700 space-y-1">
                  {digest.care_circle.new_members_this_week.slice(0, 3).map((m, i) => (
                    <li key={i}>+ {m.display_name} · {m.relationship}</li>
                  ))}
                </ul>
              )}
              <a href="/patient/care-circle" className="inline-flex items-center gap-1 text-xs font-semibold text-cn-primary mt-3">
                Open care circle <ChevronRight className="w-3 h-3" />
              </a>
            </section>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function KPI({ label, value, testid }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 p-3" data-testid={`kpi-${testid}`}>
      <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}
